import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import Collapse from '@mui/material/Collapse';
import CloseIcon from '@mui/icons-material/Close';

// import Graph from './graph/Graph';

import { Graph as LibGraph } from 'athena-lib';
import { visjsGraphOptions as defaultVisjsGraphOptions } from './graph/graph_options';

import ToolsPanel from './tools';
import Aside from './aside/Aside';
import AddNodeModal from './add_node/Modal';

import _axios from '../../../utils/axios';
import { useQuery } from "../../../utils/hooks";

import styles from '../../../styles/case/Case.module.scss';

import { getCaseFromServer, downloadString, saveCase } from './api';
import { useCaseData } from './hookData';
import { useNodeHighlights } from './hookHighlights';
import { useNodeNotes } from './hookNodeNotes';
import { groupNodes } from './nodeGrouping';
import { getAllNodesWithPosition } from './utilsData';

import TimeFilter from './time_filter/TimeFilter';
import NoteDialog from './note_dialog/NoteDialog';
import { selectEdges } from '../../../features/case.slice';
import HighlightNodes from './highlight_nodes/HighlightNodes';
import { ENABLE_MULTIPLE_AUTORUN, ENABLE_TIMEFILTER } from '../../../utils/settings';

// import TimeAxis from "./time_axis/TimeAxis";

// <div className={styles.libGraphWrapper}>
// <LibGraph
//   onVisJsNetworkCreated={() => { console.log("Created network");} }
// />
// </div>

/**
 * @param {function} props.setCaseChanges
*/
export default function Case(props) {
  const routerParams = useParams();
  const urlSearchParams = useQuery();


  const [showAddNodeModal, setShowAddNodeModal] = useState(false);
  const [showImportCaseModal, setShowImportCaseModal] = useState(false);
  const [showTimeAxisModal, setShowTimeAxisModal] = useState(false);
  const [highlightNeighbours, setHighlightNeighbours] = useState(false);

  const [isLoadingCase, setIsLoadingCase] = useState(true);
  const [caseMetadata, setCaseMetadata] = useState({});
  const [graphOptions, setGraphOptions] = useState(defaultVisjsGraphOptions);

  const [rawGraphSelection, setRawGraphSelection] = useState({});
  const [selectedNode, setSelectedNode] = useState(null);
  const [selectedNodes, setSelectedNodes] = useState(null);
  const [selectedEdge, setSelectedEdge] = useState(null);

  const [queryParamProcessed, setQueryParamProcessed] = useState(false);

  const graph = useRef();

  const [muiAlert, setMuiAlert] = useState({
    open: false,
    severity: 'success',
    message: 'Case saved successfully!'
  });

  const {
    microservices,
    graphData, setGraphDataAndRefs,
    filteredGraphData,
    searchedString, setSearchedString,
    searchInInfo, setSearchInInfo,
    timeFilter, setTimeFilter,
    filterDefaultGrouping, setFilterDefaultGrouping,

    undoData, setGraphDataToUndoLevel,

    deleteGraphData,
    upsertGraphData,
    searchNodesAuto,
    searchNodeWithFetcher
  } = useCaseData({setCaseChanges: props.setCaseChanges});

  const {
    highlightedNodes,
    highlightedNodesSearch,
    highlightedNodesSearchedLowInterval,
    setHighlightedNodesSearch,
    setHighlightedNodesSearchFocus
  } = useNodeHighlights(graphData, rawGraphSelection, highlightNeighbours, searchedString, searchInInfo, timeFilter);

  const {
    showNoteDialog, crtNoteDialogNote, crtNoteDialogParams,
    noteDlgClose, noteDlgSave, noteDlgDelete, createNote, editNote, refreshNotes,
    noteServerRequests, noteServerError
  } = useNodeNotes(graphData, upsertGraphData);

  // Load case data on mount
  useEffect(() => {
    let urlCaseName = routerParams.caseName

    setIsLoadingCase(true);
    getCaseFromServer(urlCaseName)
      .then(({ id, name, nodes, edges, createdAt, updatedAt, other }) => {
        console.log("CASE: ", { id, name, nodes, edges, createdAt, updatedAt, other });
        // nodes in existing cases don't have `value` fields
        const backwardsCompatibleNodes = nodes.map(node => ({ ...node, value: node.value || node.title || node.label }));
        const backwardsCompatibleEdges = edges.map(edge => {
          if (edge.from_type && edge.from_value != null && edge.to_type && edge.to_value != null) {
            return edge;
          }
          let [from_type, ...from_value] = edge.from.split('@'); from_value = from_value.join('@');
          let [to_type, ...to_value] = edge.to.split('@'); to_value = to_value.join('@');
          return {
            ...edge,
            from_type, from_value,
            to_type, to_value,
            visjsProps: { label: edge.label }
          };
        })
        for (let node of backwardsCompatibleNodes) {
          for (let msId in (node.trace || {})) {
            for (let fetcherName in node.trace[msId]) {
              if (node.trace[msId][fetcherName].run === 2) {
                node.trace[msId][fetcherName].run = -1; node.trace[msId][fetcherName].info = 'Interrupted';
              }
            }
          }

        }
        setGraphDataAndRefs({ nodes: backwardsCompatibleNodes, edges: backwardsCompatibleEdges, fit: true, setNodesPosition: true });
        setCaseMetadata({ id, name, createdAt, updatedAt });
        if (other && other.timeFilter) setTimeFilter(other.timeFilter);
        setIsLoadingCase(false);
        props.setCaseChanges(false);
      })
      .catch(error => {
        console.warn("Can't load case '" + urlCaseName + "' from the server: [" + error + "]. Starting new case with this name.");
        // Making new working case with the specified name, without saving it yet
        setGraphDataAndRefs({ nodes: [], edges: [] });
        setCaseMetadata({ id: '', name: urlCaseName, createdAt: '', updatedAt: '' });
        setIsLoadingCase(false);
      });
      props.setCaseChanges(false);
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routerParams.caseName]);

  // Process query parameters to automatically add a node
  useEffect(() => {
    if (isLoadingCase) return;
    if (Object.keys(microservices).length === 0) return;
    if (queryParamProcessed) return;

    const node = {
      type: urlSearchParams.get('type'),
      value: urlSearchParams.get('value')
    }
    const ms = urlSearchParams.get('ms');

    if (node.type && node.value != null) {
      if (ms) {
        let msId = null;
        for (let id in microservices) {
          if (microservices[id].name === ms) {
            msId = id;
            break;
          }
        }
        if (msId) {
          for (let fetcherObj of microservices[msId].fetchers) {
            searchNodeWithFetcher(node, msId, fetcherObj.name);
          }
        } else {
          console.warn("Can't find microservice '" + ms + "' in the case.");
        }
      } else {
        searchNodesAuto([node]);
      }
    }

    setQueryParamProcessed(true);

    // set timeout to auto select node after it's added to graph
    setTimeout(() => {
      try {
        graph.current.selectNodes([node]);
      } catch (error) {
        console.warn("Can't select node: ", error);
      }
    }, 100);
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingCase, microservices, urlSearchParams]);

  useEffect(() => {
    const { from, to } = timeFilter;
    console.log("Time filter: ", from, to);
  }, [timeFilter]);

  useEffect(() => {
    if (!muiAlert.open) return;

    const timer = setTimeout(() => {
      setMuiAlert(muiAlert => ({...muiAlert, open: false}));
    }, 3000);
    return () => clearTimeout(timer);
  }, [muiAlert]);

  return (
    <div className={styles.case}>
      <div className={styles.alert}>
        <Box sx={{ width: '100%' }}>
          <Collapse in={muiAlert.open}>
            <Alert
              severity={muiAlert.severity}
              action={
                <IconButton
                  aria-label="close"
                  color="inherit"
                  size="small"
                  onClick={() => {
                    setMuiAlert(muiAlert => ({...muiAlert, open: false}))
                  }}
                >
                  <CloseIcon fontSize="inherit" />
                </IconButton>
              }
              sx={{ mb: 2 }}
            >
              {muiAlert.message}
            </Alert>
          </Collapse>
        </Box>
      </div>

      <ToolsPanel
        caseMetadata={caseMetadata}
        unsavedChanges={props.caseChanges}
        onAddNode={() => setShowAddNodeModal(true)}
        onAddEdge={() => { console.log("TODO: add edge") }}
        
        undoData={undoData}
        undo={() => { undoData?.length > 0 && setGraphDataToUndoLevel(undoData[undoData.length - 1]); }}
        
        isRefreshNotesBusy={noteServerRequests > 0}
        refreshNotes={refreshNotes}
        hasStaleNodes={highlightedNodesSearchedLowInterval?.length > 0}
        refreshStaleNodes={() => {
          for (let staleNode of highlightedNodesSearchedLowInterval) {
            for (let msId in staleNode.trace) {
              for (let fetcherName in staleNode.trace[msId]) {
                searchNodeWithFetcher(staleNode, msId, fetcherName, graph.current.getCenterPosition([staleNode]));
              }
            }
          }
        }}

        physics={graphOptions.physics.enabled}
        setPhysics={enablePhysics => {
          setGraphOptions((crtOptions) => ({
            ...crtOptions,
            physics: { ...crtOptions.physics, enabled: enablePhysics }
          }))
        }}
        highlightNeighbours={highlightNeighbours}
        setHighlightNeighbours={setHighlightNeighbours}
        filterDefaultGrouping={filterDefaultGrouping}
        setFilterDefaultGrouping={setFilterDefaultGrouping}

        graphData={graphData}
        exportCase={() => {
          const nodesWithPosition = graph.current.getNodesWithPosition();
          downloadString(
            (caseMetadata.name || 'case') + '.json',
            JSON.stringify({
              name: caseMetadata.name || 'case',
              nodes: getAllNodesWithPosition(graphData.nodes, nodesWithPosition),
              edges: graphData.edges,
              created_at: caseMetadata.createdAt || '',
              updated_at: caseMetadata.updatedAt || ''
            })
          );
        }}
        saveCase={() => {
          const nodesWithPosition = graph.current.getNodesWithPosition();
          saveCase(caseMetadata, getAllNodesWithPosition(graphData.nodes, nodesWithPosition), graphData.edges, {timeFilter})
            .then((data) => {
              console.log("Saved case: ", data);
              setMuiAlert({
                open: true,
                severity: 'success',
                message: 'Case saved successfully!'
              });
              props.setCaseChanges(false);
            })
            .catch(error => {
              console.warn("Error saving case: ", error);
              setMuiAlert({
                open: true,
                severity: 'error',
                message: 'Failed to save the case.'
              });
            });
        }}
      />

      <LibGraph
        ref={graph}
        size={{ width: '100%', height: '100%' }}
        data={filteredGraphData}
        overrideDefaultOptions={true}
        visjsOptions={graphOptions}
        onVisJsNetworkCreated={(network, dsNodes, dsEdges) => {
          // issue in visjs: option is ignored, must set on object manually
          network.groups.options.useDefaultGroups = false;

          // hack to draw labels on top of arrows:  https://github.com/visjs/vis-network/issues/1600
          // not in athena-lib cuz it only works / is needed for node shape = image. a generic graph could have mixed nodes
          const nodeClass = network.body.functions.createNode().__proto__;
          nodeClass.draw = function(ctx) {
            var values = this.getFormattingValues();
            this.shape.draw(ctx, this.x, this.y, this.selected, this.hover, values);
            return { drawExternalLabel: () => this.shape._drawImageLabel(ctx, this.x, this.y, this.selected, this.hover) };
          };
        }}
        nodeMarkers={highlightedNodes}
        onSelectionChanged={(nodes, edges) => {
          setRawGraphSelection({ nodes, edges });
          if (nodes.length === 0 && edges.length === 0) {
            setSelectedNode(null); setSelectedNodes(null); setSelectedEdge(null);
          } else if (nodes.length === 1) {
            setSelectedNode(nodes[0]); setSelectedNodes(null); setSelectedEdge(null);
          } else if (nodes.length > 1) {
            setSelectedNode(null); setSelectedNodes([...nodes]); setSelectedEdge(null);
          } else if (nodes.length === 0 && edges.length === 1) {
            setSelectedNode(null); setSelectedNodes(null); setSelectedEdge(edges[0]);
          }
        }}
        onDoubleClickNode={node => searchNodesAuto([node], graph.current.getCenterPosition([node]))}
        onKeyDown={(e) => {
          // console.log('key_down', e);
          if (e.key === 'd') {
            deleteGraphData(rawGraphSelection.nodes, rawGraphSelection.edges);
            graph.current.selectNodes([]); // issue in visjs: not updating selection when deleting selected
          }
          if (e.key === 'z' && e.ctrlKey) {
            if (undoData.length > 0)
              setGraphDataToUndoLevel(undoData[undoData.length - 1]);
          }
          if (e.key === 'Enter') {
            const nodes = rawGraphSelection.nodes || [];
            if (ENABLE_MULTIPLE_AUTORUN) searchNodesAuto(nodes, graph.current.getCenterPosition(nodes));
          }
        }}
        enableFilter={false}
        filter={{
          node: (node) => {
            //console.log(node);
            return node.value.includes(searchedString)
          },
          edge: (edge) => true
        }}
        
      />

      {/*
      <Graph
        network={network}
        nodes={dataSetNodes.current}
        edges={dataSetEdges.current}
        physics={physics}
        setShowAside={setShowAside}
        typeAside={typeAside}
        setTypeAside={setTypeAside}
        contentAside={contentAside}
        setContentAside={setContentAside}
        nodeSource={nodeSource}
        setNodeSource={setNodeSource}
        sendRequest={sendRequest}
        addEdge={addEdge}
        customNote={customNote}
        setCustomNote={setCustomNote}
      />
      */}

      <Aside
        show={selectedNode !== null || selectedEdge !== null || selectedNodes !== null}
        content={{ type: selectedNode ? 'node' : (selectedEdge ? 'edge' : 'multiple'), node: selectedNode, edge: selectedEdge, nodes: selectedNodes }}

        timeFilter={timeFilter}
        sendRequest={(node, microserviceId, fetcher) => searchNodeWithFetcher(node, microserviceId, fetcher, graph.current.getCenterPosition([node]))}

        onAddNote={createNote}
        onEditNote={editNote}
        onDeleteNote={noteDlgDelete}
        deleteObject={obj => {
          obj.type === 'node' ? deleteGraphData([obj.node], []) : (obj.type === 'edge' ? deleteGraphData([], [obj.edge]) : deleteGraphData(obj.nodes));
          graph.current.selectNodes([]); // workaround for issue with visjs not updating selection
        }}
        setNodeLabel={(node, newLabel) => {
          const nodeUpdate = { type: node.type, value: node.value, visjsProps: { label: newLabel } };
          upsertGraphData([nodeUpdate], []);
        }}
        onGroup={(groupName) => {
          const groupedSubGraph = groupNodes(selectedNodes, groupName);
          const centerPos = graph.current.getCenterPosition(selectedNodes);
          upsertGraphData(groupedSubGraph.nodes, groupedSubGraph.edges, centerPos);
        }}
      />

      <AddNodeModal
        show={showAddNodeModal}
        setShow={setShowAddNodeModal}
        onAddNodes={(typeList, value) => searchNodesAuto(typeList.map(type => ({ type, value })))}
      />

      <NoteDialog
        show={showNoteDialog} crtNote={crtNoteDialogNote} otherParams={crtNoteDialogParams}
        onCancel={noteDlgClose} onSave={noteDlgSave} onDelete={noteDlgDelete}
      />

      {/*
      <ImportCaseModal
        show={showImportCaseModal}
        setShow={setShowImportCaseModal}
      />
      <TimeAxisModal
        show={showTimeAxisModal}
        setShow={setShowTimeAxisModal}
      />
      */}

      <HighlightNodes
        setSearchedString={setSearchedString}
        searchInInfo={searchInInfo}
        setSearchInInfo={setSearchInInfo}
        results={highlightedNodesSearch}
        setFocusedNodes={(nodes) => {
          if (nodes?.length === 1) graph.current.moveTo(graph.current.getCenterPosition(nodes));
          setHighlightedNodesSearchFocus(nodes);
        }}
      />
      { ENABLE_TIMEFILTER &&
        <TimeFilter
          value={timeFilter}
          setValue={setTimeFilter}
        />
      }
    </div>
  );
}
